import {
  AGENT_JUDGE_CATEGORICAL_MANAGER,
  AGENT_JUDGE_MANAGER,
  AGENT_JUDGE_NUMERICAL_MANAGER,
} from "../configuration/optimization/constants"
import type { MetricDirectoryEntry } from "../hooks/useMetricDirectory"

export const LLM_JUDGE_TYPE = {
  TEXT: "text",
  AUDIO: "audio",
} as const

export type LlmJudgeType = (typeof LLM_JUDGE_TYPE)[keyof typeof LLM_JUDGE_TYPE]

export const LLM_JUDGE_OUTPUT_TYPE = {
  BINARY: "binary",
  CATEGORICAL: "categorical",
  NUMERICAL: "numerical",
} as const

export type LlmJudgeOutputType =
  (typeof LLM_JUDGE_OUTPUT_TYPE)[keyof typeof LLM_JUDGE_OUTPUT_TYPE]

export const LLM_JUDGE_MODE = {
  STANDARD: "standard",
  AGENTIC: "agentic",
} as const

export type LlmJudgeMode = (typeof LLM_JUDGE_MODE)[keyof typeof LLM_JUDGE_MODE]

export interface LlmJudgeInfo {
  type: LlmJudgeType
  outputType: LlmJudgeOutputType
  mode: LlmJudgeMode
}

export function isAgentJudgeFromEntry(
  entry: MetricDirectoryEntry | undefined,
): boolean {
  return (
    entry?.metric_manager === AGENT_JUDGE_MANAGER ||
    entry?.metric_manager === AGENT_JUDGE_CATEGORICAL_MANAGER ||
    entry?.metric_manager === AGENT_JUDGE_NUMERICAL_MANAGER
  )
}

export function isLlmJudgeFromEntry(
  entry: MetricDirectoryEntry | undefined,
): boolean {
  // Persona variants share evaluator + (modality, semantic_type) with their
  // STANDARD twin; including them would clobber the reverse lookup map and
  // route users to a manager that has no FE record class.
  return entry?.evaluator === "llm_judge" && entry.variant !== "persona"
}

function isConfigurableLlmJudgeFromEntry(
  entry: MetricDirectoryEntry | undefined,
): boolean {
  return (
    isLlmJudgeFromEntry(entry) &&
    entry !== undefined &&
    [
      "manager_llm_binary",
      "manager_default_categorical",
      "manager_numerical_llm_judge",
      "manager_audio_llm_binary",
      "manager_audio_llm_categorical",
      "manager_audio_llm_numerical",
      AGENT_JUDGE_MANAGER,
      AGENT_JUDGE_CATEGORICAL_MANAGER,
      AGENT_JUDGE_NUMERICAL_MANAGER,
    ].includes(entry.metric_manager)
  )
}

// modality + semantic_type must be present on STATIC-source LLM judge classes;
// guard against partial catalog rows so callers don't blow up rendering.
export function getLlmJudgeInfoFromEntry(
  entry: MetricDirectoryEntry | undefined,
): LlmJudgeInfo | null {
  if (entry === undefined || !isConfigurableLlmJudgeFromEntry(entry)) {
    return null
  }
  const type =
    entry.modality === "audio" ? LLM_JUDGE_TYPE.AUDIO : LLM_JUDGE_TYPE.TEXT
  const outputType = toLlmJudgeOutputType(entry.semantic_type)
  if (!outputType) {
    return null
  }
  return {
    type,
    outputType,
    mode: isAgentJudgeFromEntry(entry)
      ? LLM_JUDGE_MODE.AGENTIC
      : LLM_JUDGE_MODE.STANDARD,
  }
}

function toLlmJudgeOutputType(
  semanticType: string | null | undefined,
): LlmJudgeOutputType | null {
  switch (semanticType) {
    case "binary":
      return LLM_JUDGE_OUTPUT_TYPE.BINARY
    case "categorical":
      return LLM_JUDGE_OUTPUT_TYPE.CATEGORICAL
    case "numerical":
      return LLM_JUDGE_OUTPUT_TYPE.NUMERICAL
    case undefined:
    case null:
    default:
      return null
  }
}
